import React, { useCallback, useEffect, useState } from 'react';
import BracketView, { bracketLayout } from './BattleBracket';
import BattleHallOfFame from './BattleHallOfFame';
import BattleConsole, { AdminMatch, AdminPayouts, useBattleDelete } from './BattleConsole';
import {
  BattleStyles, BattleCatalogContext, FightCard, EntryChip, PrizeLadder, SlotArt, Multi, Modal, Btn, LiveDot, label, panel, Initial,
} from './BattleParts';
import { BracketViewer } from './BattleShared';
import {
  C, MEDAL, FORMAT_META, FORMATS, isLive, useBattleFeed, useServerClock, roundLabel, fmtAmount, ordinal, fmtDate,
  focusedMatch, versusScore, slotLabel, fightTerms, EXIT_COMMAND, isThirdPlace, matchLabel, playersInPlay, totalRounds,
  fightLabels, setBattleApi, battleApi, getQuery, setQuery,
} from './battleLib';

// ============================================
// SLOT BATTLES — tournaments, team arena and Snutzy vs chat, all entered from
// Twitch chat with one command and played live on stream (route:
// /slot-battles). Public page: the live battle (bracket explorable by
// anyone), every past one, and the Legends board. Admins get the battle
// controls inline. OBS overlay: /widget/battles (BattleWidget.jsx).
// ============================================

const STEPS = {
  tournament: [
    ['💬', 'Call your slot', 'Drop the command and a slot in Twitch chat — we look it up on Shuffle for you. Changed your mind? Just send it again.'],
    ['🎯', 'Get drawn', 'Seats are drawn live from everyone who entered, two at a time. Until your name comes up you can still swap slots.'],
    ['🏆', 'Out-multi them', 'Snutzy buys both bonuses. Higher multi moves on — the top finishers get paid.'],
  ],
  arena: [
    ['💬', 'Call your slot', 'Drop the command and a slot in Twitch chat. One entry each — send it again to swap.'],
    ['🎯', 'Join a squad', 'Every battle draws two squads from the lineup — solo, duos or trios.'],
    ['⚔️', 'Squad average wins', 'Every bonus is bought on stream; the better average multi takes the prize and splits it.'],
  ],
  versus: [
    ['💬', 'Call your slot', 'Drop the command and a slot in Twitch chat. One entry each — send it again to swap.'],
    ['🎯', 'Get drawn', "Chat's side is drawn against Snutzy's own pick. In squad battles he picks teammates from the lineup too."],
    ['🎙️', 'Beat the host', "Out-multi Snutzy's side and the prize goes to chat."],
  ],
};

// A ring that fills as the lineup grows (to the bracket size when there is one).
function CountRing({ count, of }) {
  const r = 46;
  const len = 2 * Math.PI * r;
  const share = of ? Math.min(1, count / of) : 1;
  return (
    <div style={{ position: 'relative', width: 120, height: 120, flexShrink: 0 }}>
      <svg width="120" height="120" viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }} aria-hidden>
        <circle cx="60" cy="60" r={r} fill="none" stroke="rgba(168,85,247,0.15)" strokeWidth="8" />
        <circle cx="60" cy="60" r={r} fill="none" stroke="url(#sbRing)" strokeWidth="8" strokeLinecap="round" strokeDasharray={`${len * share} ${len}`} style={{ transition: 'stroke-dasharray 700ms ease' }} />
        <defs><linearGradient id="sbRing" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor={C.lav} /><stop offset="100%" stopColor={C.violet} /></linearGradient></defs>
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span key={count} style={{ fontFamily: C.display, fontSize: '1.6rem', fontWeight: 800, color: '#fff', lineHeight: 1, animation: 'sb-count 0.5s ease-out' }}>{count}</span>
        <span style={{ ...label, fontSize: '0.5rem', marginTop: 4 }}>{of ? `of ${of} seats` : 'entered'}</span>
      </div>
    </div>
  );
}

// How to get in: a mock chat line with the command, and the lineup count.
function EnterCard({ battle, count, children }) {
  return (
    <div className="sb-split">
      <div style={{ ...panel, padding: '1.4rem', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', right: -60, top: -80, width: 260, height: 260, borderRadius: '50%', background: 'radial-gradient(circle, rgba(168,85,247,0.22), transparent 65%)', pointerEvents: 'none' }} />
        <span style={{ ...label, display: 'flex', alignItems: 'center', gap: 8, color: battle.entries_open ? C.lav : C.dim }}>
          {battle.entries_open ? <><LiveDot size={7} /> entries are open</> : '🔒 entries are locked'}
        </span>
        {/* a chat message, the way it'll look in Twitch chat */}
        <div style={{ marginTop: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: '0.6rem', maxWidth: '100%', borderRadius: '16px 16px 16px 4px', padding: '0.7rem 1rem', background: 'rgba(5,5,7,0.7)', border: '1px solid rgba(168,85,247,0.35)', boxSizing: 'border-box' }}>
          <span style={{ fontSize: '0.8rem', fontWeight: 800, color: C.violetHi }}>you:</span>
          <span style={{ fontFamily: C.display, fontSize: 'clamp(1.1rem, 3.4vw, 1.7rem)', fontWeight: 800, color: '#fff' }}>
            {battle.command} <span style={{ color: C.lav, fontWeight: 600 }}>your slot</span>
          </span>
        </div>
        {children}
        {battle.entries_open && (
          <p style={{ margin: '0.9rem 0 0', fontSize: '0.76rem', color: C.muted, lineHeight: 1.55 }}>
            Send it again with a different slot to swap — your latest one counts until you're drawn.
            Changed your mind entirely? <strong style={{ color: '#fff' }}>{EXIT_COMMAND}</strong> takes you out of the lineup.
          </p>
        )}
      </div>
      <div style={{ ...panel, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
        <CountRing count={count} of={battle.format === 'tournament' && battle.status === 'lobby' ? battle.bracket_size : null} />
        <span style={{ ...label, color: C.muted }}>in the lineup</span>
      </div>
    </div>
  );
}

// The lineup: the feed carries the newest 40, "show everyone" fetches the rest.
function Lineup({ state }) {
  const [all, setAll] = useState(null);
  const entries = all || state.entries;
  useEffect(() => { setAll(null); }, [state.battle.id]);
  return (
    <div style={{ marginTop: '2rem' }}>
      <div style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '0.75rem' }}>
        <h3 style={{ margin: 0, fontFamily: C.display, fontSize: '1rem', fontWeight: 800, color: '#fff' }}>The lineup <span style={{ color: C.dim, fontWeight: 600 }}>· {state.entry_count}</span></h3>
        {state.entry_count > state.entries.length && !all && (
          <button
            type="button"
            onClick={() => battleApi(`/api/battles/${state.battle.id}/entries`).then((d) => setAll(d.entries)).catch(() => {})}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: C.font, fontSize: '0.74rem', color: C.lav, textDecoration: 'underline' }}
          >
            show everyone
          </button>
        )}
      </div>
      {entries.length === 0 ? (
        <p style={{ ...panel, margin: 0, textAlign: 'center', fontSize: '0.85rem', color: C.dim, padding: '2rem' }}>The lineup is empty — first one in gets bragging rights.</p>
      ) : (
        <div className="sb-grid sb-3 sb-scroll" style={{ maxHeight: '26rem', overflowY: 'auto', paddingRight: '4px' }}>
          {entries.map((e) => <EntryChip key={e.id} entry={e} rolled={e.rolled} />)}
        </div>
      )}
    </div>
  );
}

// Stepped plinths, tallest in the middle.
function Podium({ players }) {
  const top = [2, 1, 3].map((n) => players.find((p) => p.placement === n)).filter(Boolean);
  if (!top.length) return null;
  const height = { 1: 120, 2: 84, 3: 64 };
  return (
    <div className="sb-plinths">
      {top.map((p) => (
        <div key={p.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          {p.placement === 1 && <span style={{ fontSize: '1.6rem', marginBottom: 2 }}>👑</span>}
          <div style={{ width: p.placement === 1 ? 92 : 72 }}><SlotArt slot={p.slot} /></div>
          <span style={{ marginTop: 8, maxWidth: '100%', fontFamily: C.display, fontSize: p.placement === 1 ? '0.95rem' : '0.8rem', fontWeight: 800, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
          <span style={{ maxWidth: '100%', fontSize: '0.68rem', color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{slotLabel(p.slot)}</span>
          <div style={{
            marginTop: 8, width: '100%', height: height[p.placement], borderRadius: '14px 14px 4px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', paddingTop: 10, boxSizing: 'border-box',
            background: `linear-gradient(180deg, ${p.placement === 1 ? 'rgba(168,85,247,0.45)' : 'rgba(168,85,247,0.2)'}, rgba(168,85,247,0.04))`,
            borderTop: `3px solid ${MEDAL[p.placement]}`,
          }}>
            <span style={{ fontFamily: C.display, fontSize: '1.3rem', fontWeight: 900, color: MEDAL[p.placement] }}>{p.placement}</span>
            {p.prize_amount > 0 && <span style={{ marginTop: 2, fontSize: '0.9rem', fontWeight: 800, color: C.gold }}>{fmtAmount(p.prize_amount)}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function PickingPanel({ state }) {
  const { battle } = state;
  const alive = playersInPlay(state);
  const round = battle.picking_round;
  const picked = alive.filter((p) => (p.picked_round ?? 0) >= round).length;
  return (
    <div style={{ ...panel, marginTop: '1.5rem' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
        <div>
          <span style={label}>{roundLabel(round, battle.bracket_size)} · new slots</span>
          <p style={{ margin: '0.3rem 0 0', fontSize: '0.9rem', color: '#fff' }}>
            Still alive? Send <strong>{battle.command} your slot</strong> in chat for your next match.
          </p>
        </div>
        <div style={{ minWidth: 160, flex: '0 1 220px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.74rem', color: C.muted, marginBottom: 4 }}>
            <span>locked in</span><strong style={{ color: '#fff' }}>{picked}/{alive.length}</strong>
          </div>
          <div style={{ height: 6, borderRadius: 999, background: 'rgba(168,85,247,0.15)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${alive.length ? (picked / alive.length) * 100 : 0}%`, background: `linear-gradient(90deg, ${C.violetDeep}, ${C.lav})`, transition: 'width 600ms ease' }} />
          </div>
        </div>
      </div>
      <div className="sb-grid sb-4" style={{ marginTop: '1rem' }}>
        {alive.map((p) => {
          const done = (p.picked_round ?? 0) >= round;
          return (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', borderRadius: '14px', border: `1px solid ${done ? 'rgba(168,85,247,0.45)' : 'rgba(255,255,255,0.06)'}`, background: done ? 'rgba(168,85,247,0.08)' : 'transparent', padding: '0.45rem 0.6rem' }}>
              {done ? <SlotArt slot={p.slot} width={30} radius={5} /> : <SlotArt hidden width={30} radius={5} textSize="0.8rem" />}
              <div style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: 'block', fontSize: '0.84rem', fontWeight: 800, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                <span style={{ display: 'block', fontSize: '0.68rem', color: done ? C.lav : C.dim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {done ? slotLabel(p.slot) : 'thinking…'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const sectionTitle = { margin: '0 0 0.75rem', fontFamily: C.display, fontSize: '1rem', fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: '0.5rem' };

// `crew` (admins): the battle controls above already carry the match on
// stream, with its controls — so the public copy of it is left out.
function TournamentLive({ state, clock, onSelect, crew }) {
  const { battle, players } = state;
  const focus = focusedMatch(state);
  if (battle.status === 'lobby') {
    return (
      <>
        <EnterCard battle={battle} count={state.entry_count}>
          <p style={{ margin: '0.9rem 0 0', fontSize: '0.84rem', color: C.bright }}>
            {battle.bracket_size}-seat knockout · {battle.repick === 'each_round' ? 'fresh slot every round' : 'one slot all the way'}
            {battle.third_place ? ' · bronze match for 3rd' : ''}
          </p>
          <PrizeLadder prizes={battle.prizes} />
        </EnterCard>
        <Lineup state={state} />
      </>
    );
  }
  const alive = players.filter((p) => !p.replaced && p.eliminated_round == null).length;
  const seatsLeft = state.matches.filter((m) => m.round === 1 && m.plays.length === 0).length;
  return (
    <>
      {battle.status === 'live' && battle.entries_open && (
        <div style={{ marginBottom: '1.5rem' }}>
          <EnterCard battle={battle} count={state.entry_count}>
            <p style={{ margin: '0.9rem 0 0', fontSize: '0.84rem', color: C.bright }}>
              Seats are being drawn live — {seatsLeft} first-round {seatsLeft === 1 ? 'match' : 'matches'} left to fill.
            </p>
          </EnterCard>
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem 1rem' }}>
        <span style={{ fontFamily: C.display, fontSize: '1.05rem', fontWeight: 800, color: '#fff' }}>
          {battle.status === 'finished' ? 'Cup complete'
            : battle.third_place && battle.current_round === totalRounds(battle.bracket_size) ? 'Bronze match & Final'
              : roundLabel(battle.current_round, battle.bracket_size)}
        </span>
        {battle.status === 'live' && <span style={{ fontSize: '0.8rem', color: C.muted, borderRadius: 999, border: '1px solid rgba(168,85,247,0.3)', padding: '0.15rem 0.6rem' }}>{alive} of {battle.bracket_size} alive</span>}
        <span style={{ marginLeft: 'auto' }}><PrizeLadder prizes={battle.prizes} compact /></span>
      </div>
      {battle.status === 'finished' && <Podium players={players} />}
      {battle.status === 'live' && battle.picking_round && <PickingPanel state={state} />}
      {battle.status === 'live' && focus && !crew && (
        <div style={{ marginTop: '1.5rem' }}>
          <h3 style={sectionTitle}><LiveDot /> Live on stream</h3>
          <FightCard key={focus.id} match={focus} labels={fightLabels(focus, battle)} clock={clock} pool={state.entries} />
        </div>
      )}
      <div style={{ marginTop: '1.5rem' }}>
        {/* tall enough to show a 4/8/16 bracket whole at a readable size */}
        <BracketView state={state} onSelect={onSelect} height={Math.min(740, bracketLayout(battle.bracket_size, battle.third_place).height + 130)} />
      </div>
      {battle.status === 'live' && battle.entries_open && <Lineup state={state} />}
    </>
  );
}

// Snutzy vs chat, as a scoreboard.
function Scoreboard({ battle, score }) {
  return (
    <div style={{ ...panel, marginTop: '1.5rem', display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: '1rem', padding: '1rem 1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
        <Initial name={battle.streamer_name} size={40} />
        <span style={{ fontFamily: C.display, fontWeight: 800, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{battle.streamer_name}</span>
      </div>
      <span style={{ fontFamily: C.display, fontSize: '2.2rem', fontWeight: 900, color: '#fff', fontVariantNumeric: 'tabular-nums', letterSpacing: '2px' }}>
        {score.house}<span style={{ color: C.violet }}> : </span>{score.chat}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.75rem', minWidth: 0 }}>
        <span style={{ fontFamily: C.display, fontWeight: 800, color: '#fff' }}>Chat</span>
        <span style={{ width: 40, height: 40, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(203,213,225,0.15)', border: `2px solid ${C.silver}`, fontSize: '1.1rem' }}>💬</span>
      </div>
    </div>
  );
}

function FightsLive({ state, clock, onSelect, crew }) {
  const { battle, matches } = state;
  const pending = matches.find((m) => m.status === 'pending');
  const focus = focusedMatch(state);
  const current = pending || focus;
  const done = matches.filter((m) => m.status === 'done' && m.id !== current?.id).slice().reverse();
  return (
    <>
      {battle.status !== 'finished' && (
        <EnterCard battle={battle} count={state.entry_count}>
          {fightTerms(battle) && (
            <p style={{ display: 'inline-flex', margin: '0.9rem 0 0', borderRadius: '10px', background: 'rgba(168,85,247,0.12)', padding: '0.35rem 0.8rem', fontSize: '0.86rem', fontWeight: 700, color: '#fff' }}>
              {fightTerms(battle)}
            </p>
          )}
          <p style={{ margin: '0.75rem 0 0', fontSize: '0.82rem', color: C.muted, lineHeight: 1.5 }}>{FORMAT_META[battle.format].blurb}</p>
        </EnterCard>
      )}
      {battle.format === 'versus' && matches.some((m) => m.status === 'done') && <Scoreboard battle={battle} score={versusScore(state)} />}
      {current && !(crew && battle.status !== 'finished') && (
        <div style={{ marginTop: '1.5rem' }}>
          <h3 style={sectionTitle}>{current.status === 'pending' ? <><LiveDot /> Live on stream</> : 'Last battle'}</h3>
          <FightCard key={current.id} match={current} labels={fightLabels(current, battle)} clock={clock} pool={state.entries} />
        </div>
      )}
      {done.length > 0 && !(crew && battle.status !== 'finished') && (
        <div style={{ marginTop: '2rem' }}>
          <h3 style={sectionTitle}>Earlier tonight</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {done.map((m) => <FightRow key={m.id} match={m} battle={battle} onClick={() => onSelect(m)} />)}
          </div>
        </div>
      )}
      {battle.status !== 'finished' && <Lineup state={state} />}
    </>
  );
}

const rowBtn = {
  display: 'flex', width: '100%', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem 0.9rem', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.06)',
  background: 'rgba(255,255,255,0.02)', padding: '0.7rem 1rem', textAlign: 'left', cursor: 'pointer', fontFamily: C.font,
};

// One decided battle, as a line with its momentum.
function FightRow({ match, battle, onClick }) {
  const labels = fightLabels(match, battle);
  const w = match.winner_side;
  const nm = (won) => ({ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.86rem', fontWeight: won ? 800 : 500, color: won ? '#fff' : C.muted });
  return (
    <button type="button" className="sb-hover" onClick={onClick} style={rowBtn}>
      <span style={{ ...label, width: '3.4rem', fontSize: '0.56rem', color: C.dim }}>
        #{match.round}{match.team_size > 1 ? ` · ${match.team_size}v${match.team_size}` : ''}
      </span>
      <span style={nm(w === 'a')}>{w === 'a' && '👑 '}{labels.a}</span>
      <Multi value={match.a_score} size="0.78rem" />
      <span style={{ fontSize: '0.7rem', color: C.violet }}>◆</span>
      <Multi value={match.b_score} size="0.78rem" />
      <span style={{ ...nm(w === 'b'), textAlign: 'right' }}>{labels.b}{w === 'b' && ' 👑'}</span>
      {match.prize_amount > 0 && <span style={{ fontSize: '0.78rem', fontWeight: 800, color: C.gold }}>{fmtAmount(match.prize_amount)}</span>}
    </button>
  );
}

// A past (or the live) battle in full, for the history modal.
function BattleDetail({ state, isAdmin, onSelect, onChanged, onDeleted }) {
  const { battle } = state;
  const { busy, error, del } = useBattleDelete(onDeleted);
  return (
    <div style={{ ...panel, padding: '1.5rem', background: 'linear-gradient(180deg, rgba(30,16,48,0.98), rgba(8,6,12,0.98))' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem' }}>
        <div>
          <span style={label}>{FORMAT_META[battle.format].icon} {FORMAT_META[battle.format].label} · {fmtDate(battle.finished_at || battle.created_at)}</span>
          <h3 style={{ margin: '0.4rem 0 0', fontFamily: C.display, fontSize: '1.3rem', fontWeight: 800, color: '#fff' }}>{battle.title || `${FORMAT_META[battle.format].short} #${battle.id}`}</h3>
        </div>
        {isAdmin && battle.status === 'finished' && !state.players.some((p) => p.awarded) && (
          <Btn kind="danger" size="sm" disabled={busy} onClick={() => window.confirm('Delete this battle permanently? Only for tests and mistakes — it disappears from history and Legends.') && del(battle.id)}>
            🗑️ Delete
          </Btn>
        )}
      </div>
      {error && <p style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#fca5a5' }}>{error}</p>}
      <div style={{ marginTop: '1rem' }}>
        {battle.format === 'tournament' ? (
          <>
            <Podium players={state.players} />
            <div style={{ marginTop: '1.5rem' }}><BracketView state={state} onSelect={onSelect} height={520} /></div>
          </>
        ) : (
          <>
            {battle.format === 'versus' && <Scoreboard battle={battle} score={versusScore(state)} />}
            <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {state.matches.filter((m) => m.status === 'done').map((m) => (
                <FightRow key={m.id} match={m} battle={battle} onClick={() => onSelect(m)} />
              ))}
            </div>
          </>
        )}
      </div>
      {isAdmin && <AdminPayouts state={state} onDone={onChanged} />}
    </div>
  );
}

function History({ format, openBattle, refreshKey }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [format]);
  useEffect(() => {
    setData(null);
    battleApi(`/api/battles/history?format=${format}&page=${page}`).then(setData).catch(() => setData({ battles: [] }));
  }, [format, page, refreshKey]);

  return (
    <section style={{ marginTop: '3rem' }}>
      <h2 style={{ ...sectionTitle, fontSize: '1.2rem' }}>📜 {format === 'tournament' ? 'Past cups' : format === 'arena' ? 'Past arena nights' : 'Past Snutzy vs Chat'}</h2>
      {!data ? (
        <p style={{ fontSize: '0.85rem', color: C.dim }}>Loading…</p>
      ) : data.battles.length === 0 ? (
        <p style={{ ...panel, margin: 0, textAlign: 'center', fontSize: '0.85rem', color: C.dim, padding: '2rem' }}>Nothing here yet — the first one is coming.</p>
      ) : (
        <div className="sb-grid sb-2">
          {data.battles.map((b) => {
            const champ = b.podium?.[0];
            return (
              <button key={b.id} type="button" className="sb-hover" onClick={() => openBattle(b.id)} style={{ ...rowBtn, flexWrap: 'nowrap', alignItems: 'stretch', padding: '0.8rem' }}>
                <div style={{ width: 44, flexShrink: 0 }}>
                  {champ ? <SlotArt slot={{ name: champ.slot, image: champ.image }} radius={8} /> : <div style={{ fontSize: '1.6rem', textAlign: 'center' }}>{FORMAT_META[b.format].icon}</div>}
                </div>
                <span style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <span style={{ fontSize: '0.92rem', fontWeight: 800, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {b.title || `${FORMAT_META[b.format].short} #${b.id}`}
                  </span>
                  <span style={{ fontSize: '0.7rem', color: C.dim }}>
                    {fmtDate(b.finished_at)} · {b.entry_count} entered · {b.matches_played} {b.matches_played === 1 ? 'battle' : 'battles'}
                  </span>
                  {champ && <span style={{ marginTop: 3, fontSize: '0.78rem', color: C.bright }}>👑 <strong>{champ.name}</strong></span>}
                  {b.format === 'versus' && (
                    <span style={{ marginTop: 3, fontSize: '0.78rem', color: C.bright }}>
                      {b.streamer_name} <strong>{b.house_wins}</strong> : <strong>{b.matches_played - b.house_wins}</strong> chat
                    </span>
                  )}
                </span>
                {b.prize_total > 0 && <span style={{ alignSelf: 'center', fontWeight: 800, color: C.gold, fontVariantNumeric: 'tabular-nums' }}>{fmtAmount(b.prize_total)}</span>}
              </button>
            );
          })}
        </div>
      )}
      {data && (page > 1 || data.has_more) && (
        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
          <Btn disabled={page === 1} onClick={() => setPage((p) => p - 1)}>‹ Newer</Btn>
          <Btn disabled={!data.has_more} onClick={() => setPage((p) => p + 1)}>Older ›</Btn>
        </div>
      )}
    </section>
  );
}

// Brackets from before the three formats (one bracket down to N winners, with
// a grand finale) — read-only, in their original layout.
function LegacyArchive({ slotCatalog }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  useEffect(() => {
    battleApi(`/api/battles/legacy?page=${page}`).then(setData).catch(() => setData({ battles: [], total: 0 }));
  }, [page]);
  if (!data || !data.total) return null;
  const pages = Math.max(1, Math.ceil(data.total / (data.pageSize || 8)));
  return (
    <section style={{ marginTop: '2rem' }}>
      <button type="button" onClick={() => setOpen((v) => !v)} style={{ ...btn0, ...label, color: C.muted }}>
        🗂️ Classic brackets · {data.total} {open ? '▴' : '▾'}
      </button>
      {open && (
        <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {data.battles.map((b) => (
            <button key={b.id} type="button" className="sb-hover" style={rowBtn} onClick={() => battleApi(`/api/battles/legacy/${b.id}`).then(setView).catch(() => {})}>
              <span style={{ minWidth: '12rem', flex: 1 }}>
                <span style={{ display: 'block', fontSize: '0.88rem', fontWeight: 800, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  👑 {(b.winners || []).map((w) => w.display_name).join(', ') || 'Unknown'}
                </span>
                <span style={{ fontSize: '0.68rem', color: C.dim }}>
                  {b.entrant_count}-player bracket{b.prize_text ? ` · ${b.prize_text}` : ''} · {fmtDate(b.finished_at)}
                </span>
              </span>
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: C.lav }}>view bracket ›</span>
            </button>
          ))}
          {pages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem' }}>
              <Btn size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Prev</Btn>
              <span style={{ fontSize: '0.72rem', color: C.dim }}>Page {page} / {pages}</span>
              <Btn size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next ›</Btn>
            </div>
          )}
        </div>
      )}
      {view && (
        <Modal wide onClose={() => setView(null)}>
          <div style={{ ...panel, background: 'rgba(10,8,16,0.98)' }}>
            <span style={label}>classic bracket · {fmtDate(view.battle.finished_at)}</span>
            <h3 style={{ margin: '0.4rem 0 1rem', fontFamily: C.display, fontSize: '1.1rem', fontWeight: 800, color: '#fff' }}>
              👑 {[...view.players.filter((p) => p.is_winner)].sort((a, b) => (a.finale_place || 99) - (b.finale_place || 99)).map((w) => w.display_name).join(', ') || 'Battle bracket'}
            </h3>
            <BracketViewer battle={view.battle} players={view.players} matches={view.matches} slotCatalog={slotCatalog} height={480} />
          </div>
        </Modal>
      )}
    </section>
  );
}
const btn0 = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: C.font };

function HowItWorks({ tab }) {
  return (
    <div style={{ marginTop: '2rem', display: 'grid', gap: '0.75rem', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
      {STEPS[tab].map(([icon, t, d], i) => (
        <div key={t} style={{ display: 'flex', gap: '0.8rem', alignItems: 'flex-start', borderRadius: '18px', padding: '1rem', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
          <span style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', background: 'rgba(168,85,247,0.14)', border: '1px solid rgba(168,85,247,0.3)' }}>{icon}</span>
          <span>
            <span style={{ display: 'block', fontFamily: C.display, fontSize: '0.8rem', fontWeight: 800, color: '#fff' }}>{i + 1}. {t}</span>
            <span style={{ display: 'block', marginTop: '0.3rem', fontSize: '0.76rem', lineHeight: 1.5, color: C.muted }}>{d}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

export default function SlotBattlesPage({ apiUrl, getToken, isAdmin, slotCatalog = [] }) {
  setBattleApi(apiUrl, getToken);
  const { state, reload } = useBattleFeed();
  const clock = useServerClock(state);
  const b = state?.battle;
  const live = isLive(b);

  // The tab follows the live battle unless someone picked one explicitly.
  // "hall" is the Legends tab (kept as ?mode=hall so old links work).
  const [modeParam, setModeParam] = useState(() => getQuery('mode'));
  const hall = modeParam === 'hall';
  const tab = FORMATS.includes(modeParam) ? modeParam : live ? b.format : (b?.format || 'tournament');
  const setTab = (m) => { setQuery({ mode: m }); setModeParam(m); };

  // Detail modal: a battle from history, and/or one match inside it.
  const [detail, setDetail] = useState(null); // state of an opened battle
  const [matchSel, setMatchSel] = useState(null); // { battleId, matchId }
  const [historyKey, setHistoryKey] = useState(0);

  const openBattle = useCallback((id) => {
    battleApi(`/api/battles/${id}`).then((d) => setDetail(d.state)).catch(() => {});
  }, []);
  // ?battle=<id> (the Discord results post links here) opens that battle
  const [linkedBattle] = useState(() => getQuery('battle'));
  useEffect(() => {
    if (/^\d+$/.test(linkedBattle || '')) openBattle(linkedBattle);
  }, [linkedBattle, openBattle]);

  const refreshDetail = useCallback(() => {
    reload();
    setHistoryKey((k) => k + 1);
    if (detail) openBattle(detail.battle.id);
  }, [reload, detail, openBattle]);

  // A selected match is looked up live, so admin edits land in the modal.
  const sourceState = matchSel && (detail?.battle.id === matchSel.battleId ? detail : state);
  const selMatch = sourceState?.matches.find((m) => m.id === matchSel.matchId);

  const recentFinished = b?.status === 'finished' && b.format === tab
    && Date.now() - new Date(b.finished_at).getTime() < 6 * 3600 * 1000;
  const showState = (live && b.format === tab) || recentFinished ? state : null;

  return (
    <BattleCatalogContext.Provider value={slotCatalog}>
      <BattleStyles />
      <div style={{ fontFamily: C.font, maxWidth: '1150px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.4rem' }}>
          <h1 style={{ margin: 0, fontFamily: C.display, fontWeight: 900, fontSize: 'clamp(1.8rem, 5vw, 3rem)', letterSpacing: '-0.5px', background: C.headline, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Slot Battles
          </h1>
          <p style={{ margin: '0.6rem auto 0', maxWidth: '40rem', fontSize: '0.95rem', color: C.muted }}>
            Chat calls the slots, Snutzy buys the bonuses, the biggest multi wins. Knockout cups, squad battles and your shot at the host himself.
          </p>
        </div>

        {/* the modes + Legends, as one segmented control */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
          <div className="sb-seg" role="tablist">
            {[...FORMATS, 'hall'].map((k) => {
              const on = k === 'hall' ? hall : !hall && tab === k;
              const isLiveTab = live && b.format === k;
              const meta = k === 'hall' ? { label: 'Legends', icon: '🌟' } : FORMAT_META[k];
              return (
                <button
                  key={k}
                  role="tab"
                  aria-selected={on}
                  type="button"
                  onClick={() => setTab(k)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.45rem', borderRadius: 999, padding: '0.55rem 1.05rem', border: 'none', cursor: 'pointer',
                    fontFamily: C.font, fontSize: '0.86rem', fontWeight: 700, whiteSpace: 'nowrap', transition: 'background 200ms, color 200ms',
                    background: on ? `linear-gradient(135deg, ${C.violet}, ${C.violetDeep})` : 'transparent',
                    color: on ? '#fff' : C.muted, boxShadow: on ? '0 4px 18px -6px rgba(168,85,247,0.9)' : 'none',
                  }}
                >
                  <span>{meta.icon}</span>{meta.label}
                  {isLiveTab && <span style={{ ...label, fontSize: '0.5rem', color: '#fff', background: 'rgba(239,68,68,0.85)', borderRadius: 999, padding: '2px 6px' }}>live</span>}
                </button>
              );
            })}
          </div>
        </div>

        {hall ? (
          // refetches when a battle is decided or one changes state
          <BattleHallOfFame refreshKey={`${b?.id}:${b?.status}:${state?.matches?.filter((m) => m.status === 'done').length ?? 0}:${historyKey}`} />
        ) : (
          <>
            {/* The crew's workspace goes first — during a battle it's what
                Snutzy works in; the public view below is for reference. */}
            {isAdmin && (
              <BattleConsole state={state} reload={() => { reload(); setHistoryKey((k) => k + 1); }} defaultFormat={tab} />
            )}

            <section style={{ marginTop: '1.5rem' }}>
              {state === undefined ? (
                <p style={{ fontSize: '0.85rem', color: C.dim, textAlign: 'center' }}>Loading…</p>
              ) : showState ? (
                <>
                  {b.title && <h2 style={{ margin: '0 0 1rem', fontFamily: C.display, fontSize: '1.3rem', fontWeight: 800, color: '#fff' }}>{b.title}</h2>}
                  {tab === 'tournament'
                    ? <TournamentLive state={state} clock={clock} crew={isAdmin} onSelect={(m) => setMatchSel({ battleId: b.id, matchId: m.id })} />
                    : <FightsLive state={state} clock={clock} crew={isAdmin} onSelect={(m) => setMatchSel({ battleId: b.id, matchId: m.id })} />}
                </>
              ) : (
                <div style={{ ...panel, padding: '1.75rem', display: 'flex', gap: '1.25rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '2.6rem' }}>{FORMAT_META[tab].icon}</span>
                  <div style={{ flex: 1, minWidth: '14rem' }}>
                    <span style={{ fontFamily: C.display, fontSize: '1.05rem', fontWeight: 800, color: '#fff' }}>{FORMAT_META[tab].label}</span>
                    <p style={{ margin: '0.4rem 0 0', maxWidth: '42rem', fontSize: '0.9rem', color: C.bright }}>{FORMAT_META[tab].blurb}</p>
                    <p style={{ margin: '0.5rem 0 0', fontSize: '0.82rem', color: C.muted }}>
                      {live ? `A ${FORMAT_META[b.format].label} is running right now — ` : 'Nothing running right now — '}
                      {live ? (
                        <button type="button" onClick={() => setTab(b.format)} style={{ ...btn0, fontSize: '0.82rem', color: C.lav, textDecoration: 'underline' }}>
                          take me there
                        </button>
                      ) : (
                        <>they kick off live on <a href="https://twitch.tv/snutzy" target="_blank" rel="noreferrer noopener" style={{ color: C.lav }}>twitch.tv/snutzy</a>.</>
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* how it works — viewers only; the crew knows */}
              {!isAdmin && <HowItWorks tab={tab} />}
            </section>

            <History format={tab} openBattle={openBattle} refreshKey={historyKey} />
            {tab === 'tournament' && <LegacyArchive slotCatalog={slotCatalog} />}
          </>
        )}
      </div>

      {detail && !matchSel && (
        <Modal
          wide
          onClose={() => {
            setDetail(null);
            if (linkedBattle) setQuery({ battle: null });
          }}
        >
          <BattleDetail
            state={detail}
            isAdmin={isAdmin}
            onSelect={(m) => setMatchSel({ battleId: detail.battle.id, matchId: m.id })}
            onChanged={refreshDetail}
            onDeleted={() => { setDetail(null); reload(); setHistoryKey((k) => k + 1); }}
          />
        </Modal>
      )}

      {matchSel && selMatch && (
        <Modal onClose={() => setMatchSel(null)}>
          <span style={label}>
            {sourceState.battle.format === 'tournament'
              ? (isThirdPlace(selMatch, sourceState.battle) || selMatch.round === totalRounds(sourceState.battle.bracket_size)
                ? matchLabel(selMatch, sourceState.battle)
                : `${roundLabel(selMatch.round, sourceState.battle.bracket_size)} · match ${selMatch.idx + 1}`)
              : `battle #${selMatch.round}`}
          </span>
          <div style={{ marginTop: '0.5rem' }}>
            {isAdmin && (isLive(sourceState.battle) || sourceState.battle.status === 'finished') ? (
              <AdminMatch match={selMatch} state={sourceState} onDone={refreshDetail} />
            ) : (
              <div style={{ background: 'rgba(10,8,16,0.98)', borderRadius: 20 }}>
                <FightCard match={selMatch} labels={fightLabels(selMatch, sourceState.battle)} />
              </div>
            )}
          </div>
          {selMatch.plays.length === 0 && (
            <p style={{ marginTop: '0.75rem', textAlign: 'center', fontSize: '0.84rem', color: C.dim }}>This one fills in as earlier rounds finish.</p>
          )}
        </Modal>
      )}
    </BattleCatalogContext.Provider>
  );
}
